import { useSelectedAgency } from 'hooks'
import TeamPageHeader from '../TeamPageHeader/TeamPageHeader'
import { TeamPageTabs } from '../TeamPageHeader/TeamPageTabs'
import {
  Team,
  useGetAgencyMembers,
  useGetTeamsForAgency,
} from '@urbanx/agx.agencies.client'
import { useCallback, useMemo } from 'react'
import {
  AgxButton,
  AgxColumn,
  AgxEmptyState,
  AgxRow,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  IColumnConfig,
  Icon_PlusOutline,
} from '@urbanx/agx.ui-components2'
import { useNavigate } from 'react-router-dom'
import LoadingState from 'components/loadingState/LoadingState'
import { EntityImage } from '@urbanx/agx-ui-components'

const tableColumns: (string | IColumnConfig)[] = [
  'Name',
  'Email',
  { name: 'Team', align: 'right' },
]

const TeamMembersPage = () => {
  const { agency: selectedAgency } = useSelectedAgency()

  const { data: agencyMembers, isPending: isAgencyMembersPending } =
    useGetAgencyMembers(selectedAgency?.id ?? '', !!selectedAgency?.id)
  const { data: agencyTeams, isPending: isTeamsForAgencyPending } =
    useGetTeamsForAgency(selectedAgency?.id ?? '', !!selectedAgency?.id)

  const activeAgencyMembers = useMemo(() => {
    return agencyMembers?.filter((a) => !a.softDeleted) || []
  }, [agencyMembers])

  const noAgencyMembers = useMemo(() => {
    return (
      !isAgencyMembersPending &&
      activeAgencyMembers &&
      activeAgencyMembers.length === 0
    )
  }, [isAgencyMembersPending, activeAgencyMembers])

  const isAnythingLoading = useMemo(() => {
    return isAgencyMembersPending || isTeamsForAgencyPending
  }, [isAgencyMembersPending, isTeamsForAgencyPending])

  const navigate = useNavigate()

  const onAddTeamMemberPressed = useCallback(() => {
    navigate(`./add`)
  }, [navigate])

  const rows = useMemo(() => {
    if (isAnythingLoading) return []

    return activeAgencyMembers?.map((member) => {
      const firstTwoTeams =
        member.teams
          ?.slice(0, 2)
          .map((teamId) => agencyTeams?.find((t) => t.id === teamId))
          .filter((team): team is Team => !!team) ?? []

      return {
        Name: (
          <AgxRow gap="spacing-xl">
            <EntityImage
              addRingToImage={true}
              photoUri={member?.photo?.publicPath}
              name={member.firstName}
            />
            <AgxColumn gap="spacing-xxs">
              <AgxTypography
                id={`txtAgentName-${member.id}`}
                type={'body_ui'}
                size={'medium'}
                color="var(--text-primary)"
              >{`${member.firstName} ${member.lastName}`}</AgxTypography>
              <AgxTypography
                id={`txtAgentRole-${member.role}`}
                type={'caption'}
                size={'medium'}
                color="var(--text-secondary)"
              >{`${member.role}`}</AgxTypography>
            </AgxColumn>
          </AgxRow>
        ),
        Email: (
          <AgxTypography
            id={`txtAgentRole-${member.email}`}
            type={'body_ui'}
            size={'small'}
            color="var(--text-secondary)"
          >{`${member.email}`}</AgxTypography>
        ),
        Team: (
          <AgxRow
            gap="spacing-md"
            justifyContent="right"
            alignItems="alignCenter"
          >
            {firstTwoTeams.map((team) => (
              <div key={`teamId-${team.id}`} className="agx-tag">
                <AgxTypography
                  id={`txtAgentteam-${team.id}`}
                  type={'tag'}
                  size={'medium'}
                  color="var(--text-primary)"
                >{`${team.name}`}</AgxTypography>
              </div>
            ))}
            {member.teams && member.teams.length > 2 && (
              <AgxRow alignItems="alignCenter">
                <AgxTypography
                  id={`txtAgentRemainingTeams`}
                  type={'tag'}
                  size={'medium'}
                  color="var(--text-primary)"
                >{`+${member.teams.length - 2}`}</AgxTypography>
              </AgxRow>
            )}
          </AgxRow>
        ),
      }
    })
  }, [activeAgencyMembers, agencyTeams, isAnythingLoading])

  const onRowClick = useCallback(
    (rowIndex: number) => {
      if (activeAgencyMembers.length <= rowIndex) return

      const memberId = activeAgencyMembers[rowIndex]?.id ?? ''

      navigate(`./edit/${memberId}`)
    },
    [activeAgencyMembers, navigate]
  )

  return (
    <AgxSpacer fill gap="spacing-xl">
      <TeamPageHeader
        selectedTab={TeamPageTabs.TeamMembers}
        actionButtons={
          <AgxButton
            id="addNewTeamMemberActionButton"
            text="New Team Member"
            variant="primary"
            size="medium"
            rightIcon={
              <Icon_PlusOutline color="#ffffff" height={12} width={12} />
            }
            onClick={onAddTeamMemberPressed}
          />
        }
      />

      {isAnythingLoading ? (
        <LoadingState />
      ) : noAgencyMembers ? (
        <AgxSpacer
          direction="horizontal"
          fill
          contentAlignment="center"
          contentJustification="center"
        >
          <AgxEmptyState
            variant="advanced"
            label="No team member found."
            description="Start by adding a New team member to get started."
            glyphType={'ghost'}
            actionButtons={[
              {
                isPrimary: true,
                label: 'New team member',
                order: 1,
                onClick: onAddTeamMemberPressed,
                rightIcon: (
                  <Icon_PlusOutline color="#ffffff" height={12} width={12} />
                ),
              },
            ]}
          />
        </AgxSpacer>
      ) : (
        <AgxTable
          variant="minimal"
          columns={tableColumns}
          rows={rows}
          onRowClick={onRowClick}
        />
      )}
    </AgxSpacer>
  )
}

export default TeamMembersPage
